import React, { useEffect, useImperativeHandle, useState } from "react";
import { TextField, Autocomplete, createFilterOptions, Checkbox } from "@mui/material";
import { styled } from '@mui/material/styles';

interface Item {
  label: string;
  value: string;
}

interface MultiSelectAutoCompleteProps {
  items?: Array<Item>;
  label?: string;
  placeholder?: string;
  selectAllLabel?: string;
  noOptionsText?: string;
  limitTags?: number;
  onChange?: (values: Array<Item>) => void;
  disabled?: boolean;
  selectionLimitReached?: boolean;
  selectedValues?: Item[]
}

export interface ChildComponentRef {
  clearSelection: () => void;
}

const CustomTextField = styled(TextField)({
  '& .MuiInputLabel-root': {
    fontSize: '12px', // Change the label font size
  },
  '& .MuiOutlinedInput-root .MuiInputBase-input': {
    fontSize: '12px', // Change the input text size
  },
});

const MultiSelectAutoCompleteV1 = React.forwardRef<ChildComponentRef, MultiSelectAutoCompleteProps>(({
  items = [],
  label,
  placeholder,
  selectAllLabel = "Select All",
  limitTags = 1,
  onChange,
  disabled = false,
  selectionLimitReached = false,
  selectedValues
}, ref) => {
  const selectAllOption = { label: selectAllLabel, value: "select-all" };
  const updatedSelectionList = [selectAllOption, ...items];
  const [values, setValues] = useState<Array<Item>>(selectedValues ?? []);
  const [checkAll, setCheckAll] = useState(false);
  const [inputValue, setInputValue] = useState("");
  
  useEffect(() => {
    if (onChange) {
      onChange(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    setCheckAll(values?.length === items?.length);
  }, [values, items?.length]);

  const filterOptions = createFilterOptions<Item>();

  const handleChange = (event: React.ChangeEvent<{}>, newValue: Array<Item>, reason: string) => {
    if (newValue && newValue?.some(option => option.value === 'select-all')) {
      const visibleOptions = filterOptions(updatedSelectionList, {
        inputValue,
        getOptionLabel: (option) => option.label
      }).filter(option => option.value !== 'select-all');

      const newCheckAll = !checkAll;
      setCheckAll(newCheckAll);

      const uniqueValues = newCheckAll
        ? Array.from(new Map([...values, ...visibleOptions].map(item => [item.value, item])).values())
        : values.filter(value => !visibleOptions.some(option => option.value === value.value));

      setValues(uniqueValues);
    } else {
      const newValues = newValue && newValue?.filter(item => item.value !== 'select-all');
      const uniqueValues = newValues && Array.from(new Map([...newValues].map(item => [item.value, item])).values());
      setValues(uniqueValues);
    }

    //setInputValue(inputValue);
  };

  const clearSelection = () => {
    setValues([]);
    setCheckAll(false);
  };

  useImperativeHandle(ref, () => ({
    clearSelection
  }));
  return (
    <>
     <Autocomplete
      multiple
      limitTags={limitTags}
      options={updatedSelectionList}
      value={values}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason !== 'reset') {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={(options, params) => {
        const filtered = filterOptions(options, params);
        if (!params.inputValue || filtered.some(option => option.value === 'select-all')) {
          return filtered;
        }
        return [selectAllOption, ...filtered];
      }}
      onChange={(event, newValue, reason) => {
        handleChange(event, newValue, reason);
      }}
      onBlur={() => {
        setInputValue("");
      }}
      getOptionDisabled={() => {return selectionLimitReached}}
      getOptionLabel={(option) => option.label}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            style={{ marginRight: 8 }}
            checked={selected || (option.value === 'select-all' && checkAll)}
          />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <CustomTextField {...params} label={label} placeholder={placeholder}
        />
      )}
      style={{ width: "100%" }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      disableCloseOnSelect
      clearOnBlur={false} 
      handleHomeEndKeys 
      disabled={disabled}
    />
    {selectionLimitReached ? <span style={{ color: '#EB0A1E', fontSize: '12px' }}>Selection Limit Reached</span> : null}
    </>
  );
});

export default MultiSelectAutoCompleteV1;
